import { useMutation } from '@tanstack/react-query';
import { Bot, ChevronDown, ChevronRight, Unplug } from 'lucide-react';
import { useEffect, useId, useRef, useState } from 'react';
import { ErrorCodes, errorCode, errorText, useToast } from '../../components/ErrorToast';
import { AlertDialog, Badge, Button, Card, EmptyState, Steps } from '../../components/ui';
import { ApiError, reportClientError } from '../../lib/errors';
import { ruDateTime } from '../../lib/format';
import { type MyAgent, type MyAgentState, isGone, revokeAgent } from './api';
import { Count, Fact, SectionHeading, shouldReport } from './parts';
import { HOW_TO, LABEL, STATE_LABEL, T, TITLE, say } from './texts';

/*
 * Раздел «Подключения» страницы «Мои ИИ-клиенты» (005, T059, T147; spec.md, US7; contracts/http-api.md): подключения
 * ИИ-клиентов из ответа `GET /api/me/agents` (`agents`). Раздел сам ведёт отзыв: окно подтверждения, запрос, список
 * заново, сообщение и фокус. Раздел «Компьютеры» 006 встанет рядом своим компонентом — этот раздел для него не меняется.
 * - Действующие подключения — сверху, у каждого «Отозвать». Число в заголовке — действующие.
 * - Прошлые (отозваны, срок истёк) — своя группа, свёрнута: сервер хранит их все, после каждого нового входа агента их
 *   больше. Без кнопок: агент по ним не работает. Порядок — как у сервера, новые сверху.
 * - Подключений нет — «Подключений нет» и как подключить. Действующих нет, прошлые есть — как подключить заново.
 * - Сведения подключения: клиент и хост его документа, права (тексты с сервера), дата подключения, последний вызов,
 *   состояние. Время — по времени объекта.
 * - Один интерфейс на 375/768/1280 px: на компьютере сведения подключения — в одну строку, до 900 px — права отдельной
 *   строкой, на телефоне «Отозвать» во всю ширину под сведениями; кнопки на телефоне — 40 px.
 */

const SCREEN = 'MyAgents';

const STATE_TONE: Record<MyAgentState, 'ok' | 'neutral'> = {
  active: 'ok',
  revoked: 'neutral',
  expired: 'neutral',
};

export function AgentsSection({
  agents,
  tz,
  reload,
}: {
  agents: readonly MyAgent[];
  tz: string;
  /** Список заново: после отзыва и после `404` (подключение отозвали раньше). */
  reload: () => Promise<unknown>;
}) {
  const headingId = useId();
  const headingRef = useRef<HTMLHeadingElement>(null);
  const [focusTick, setFocusTick] = useState(0);
  const [target, setTarget] = useState<MyAgent | null>(null);
  const { showInfo } = useToast();
  const active = agents.filter((a) => a.state === 'active');
  const past = agents.filter((a) => a.state !== 'active');

  useEffect(() => {
    if (focusTick > 0) headingRef.current?.focus();
  }, [focusTick]);

  /** Отзыв закончен: окно закрыто, сообщение, фокус — на заголовке (его эффект идёт после эффекта окна). */
  const finish = (text: string) => {
    setTarget(null);
    showInfo(text);
    setFocusTick((t) => t + 1);
  };

  const revoke = useMutation({
    mutationFn: (a: MyAgent) => revokeAgent(a.grantId),
    // Список заново до закрытия окна: строка уходит в прошлые вместе с окном.
    onSuccess: async (_, a) => {
      await reload();
      finish(say.revoked(a.client.name));
    },
    onError: async (e, a) => {
      if (isGone(e)) {
        await reload();
        finish(say.alreadyRevoked(a.client.name));
        return;
      }
      if (shouldReport(e)) reportClientError(SCREEN, 'revoke', e);
    },
  });

  const open = (a: MyAgent) => {
    revoke.reset();
    setTarget(a);
  };

  const busy = revoke.isPending;
  const error = revoke.error && !isGone(revoke.error) ? revoke.error : null;
  return (
    // `gap`, а не `space-y`: закрытое окно (`display: none`) не даёт лишнего отступа.
    <section aria-labelledby={headingId} className="flex flex-col gap-3">
      <SectionHeading id={headingId} ref={headingRef} title={TITLE.list} count={active.length} />
      {agents.length === 0 ? (
        <Empty />
      ) : (
        <>
          {active.length > 0 ? (
            <AgentList label={TITLE.list} agents={active} tz={tz} onRevoke={open} />
          ) : (
            <Card>
              <HowTo intro={T.noActive} />
            </Card>
          )}
          {past.length > 0 && <PastGroup agents={past} tz={tz} />}
        </>
      )}
      <AlertDialog
        open={target !== null}
        // Пока идёт отзыв, окно не закрывается: итог покажет сообщение.
        onClose={() => {
          if (!busy) setTarget(null);
        }}
        title={target ? say.confirmTitle(target.client.name) : ''}
        icon={Unplug}
        tone="bad"
        description={
          <>
            {T.confirm}
            {error && <RevokeError error={error} />}
          </>
        }
      >
        <Button data-autofocus disabled={busy} onClick={() => setTarget(null)}>
          {LABEL.cancel}
        </Button>
        <Button variant="danger" disabled={busy} onClick={() => target && revoke.mutate(target)}>
          {busy ? LABEL.revoking : LABEL.revokeConfirm}
        </Button>
      </AlertDialog>
    </section>
  );
}

/* ---------- Списки ---------- */

function AgentList({
  label,
  agents,
  tz,
  onRevoke,
}: {
  label: string;
  agents: readonly MyAgent[];
  tz: string;
  /** Есть у действующих: у строки кнопка «Отозвать». */
  onRevoke?: (a: MyAgent) => void;
}) {
  // Подключений одного клиента несколько — у кнопки для чтения с экрана ещё и время подключения.
  const seen = new Map<string, number>();
  for (const a of agents) seen.set(a.client.name, (seen.get(a.client.name) ?? 0) + 1);
  return (
    <Card>
      <ul aria-label={label} className="divide-y divide-line">
        {agents.map((a) => (
          <AgentRow
            key={a.grantId}
            a={a}
            tz={tz}
            twin={(seen.get(a.client.name) ?? 0) > 1}
            onRevoke={onRevoke && (() => onRevoke(a))}
          />
        ))}
      </ul>
    </Card>
  );
}

/** Прошлые подключения: кнопка группы с числом; список и пояснение — по нажатию. */
function PastGroup({ agents, tz }: { agents: readonly MyAgent[]; tz: string }) {
  const [open, setOpen] = useState(false);
  const id = useId();
  return (
    <div className="flex flex-col gap-3">
      <div>
        {/* Ghost-кнопка без своего отступа слева: стрелка — на одной линии с заголовком раздела. */}
        <Button
          variant="ghost"
          icon={open ? ChevronDown : ChevronRight}
          aria-expanded={open}
          aria-controls={id}
          className="-ml-3"
          onClick={() => setOpen((o) => !o)}
        >
          {TITLE.past}
          <Count n={agents.length} />
        </Button>
      </div>
      <div id={id} hidden={!open} className="space-y-3">
        <p className="text-[13px] text-ink-2">{T.past}</p>
        <AgentList label={TITLE.past} agents={agents} tz={tz} />
      </div>
    </div>
  );
}

function AgentRow({
  a,
  tz,
  twin,
  onRevoke,
}: {
  a: MyAgent;
  tz: string;
  twin: boolean;
  onRevoke?: () => void;
}) {
  const nameId = useId();
  const connected = ruDateTime(a.connectedAt, tz);
  return (
    <li aria-labelledby={nameId} className="flex flex-wrap items-start gap-x-3 gap-y-3 px-6 py-4 max-sm:px-4">
      <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] bg-accent-soft text-accent">
        <Bot size={18} strokeWidth={1.75} aria-hidden />
      </span>
      <div className="min-w-0 flex-1">
        <h3 id={nameId} className="text-[15px] font-semibold break-words text-ink">
          {a.client.name}
        </h3>
        <p className="mt-0.5 text-[12.5px] text-ink-3">
          <span className="sr-only">{LABEL.clientDoc} </span>
          <span className="font-mono break-all">{a.client.host}</span>
        </p>
      </div>
      {onRevoke && (
        <Button
          icon={Unplug}
          aria-label={say.revokeName(a.client.name, twin ? connected : undefined)}
          className="max-sm:order-last max-sm:w-full"
          onClick={onRevoke}
        >
          {LABEL.revoke}
        </Button>
      )}
      <dl className="grid basis-full grid-cols-[minmax(0,2fr)_repeat(3,minmax(0,1fr))] gap-x-6 gap-y-3 pl-12 max-tablet:grid-cols-3 max-sm:grid-cols-2 max-sm:gap-x-4 max-sm:pl-0">
        <Fact label={LABEL.scopes} className="max-tablet:col-span-3 max-sm:col-span-2">
          <ul aria-label={LABEL.scopes} className="space-y-1">
            {a.scopes.map((s) => (
              <li key={s.scope}>{s.text}</li>
            ))}
          </ul>
        </Fact>
        <Fact label={LABEL.connectedAt}>
          <span className="tabular-nums">{connected}</span>
        </Fact>
        <Fact label={LABEL.lastUsedAt}>
          {a.lastUsedAt ? (
            <span className="tabular-nums">{ruDateTime(a.lastUsedAt, tz)}</span>
          ) : (
            <span className="text-ink-3">{LABEL.noCalls}</span>
          )}
        </Fact>
        <Fact label={LABEL.state}>
          <Badge tone={STATE_TONE[a.state]} dot>
            {STATE_LABEL[a.state]}
          </Badge>
        </Fact>
      </dl>
    </li>
  );
}

/** Ошибка отзыва в окне: окно остаётся, текст и код ошибки (requestId) для команды разработки. */
function RevokeError({ error }: { error: Error }) {
  const requestId = error instanceof ApiError ? error.requestId : null;
  const code = errorCode(error);
  return (
    <span role="alert" className="mt-3 block rounded-control bg-bad-soft px-3 py-2 text-[13px] text-bad">
      <span className="block font-medium">{T.revokeFailed}.</span>
      <span className="block">{errorText(error)}</span>
      {(requestId || code) && (
        <span className="mt-0.5 block text-[12px]">
          {LABEL.errorCode} <ErrorCodes requestId={requestId} code={code} />
        </span>
      )}
    </span>
  );
}

/* ---------- Как подключить ---------- */

/** Как подключить ИИ-агента: по порядку. Ссылки на инструкцию нет: такой страницы у табеля нет. */
function HowTo({ intro }: { intro?: string }) {
  const id = useId();
  return (
    <section aria-labelledby={id} className="px-6 pt-5 pb-6 max-sm:px-4">
      <h3 id={id} className="text-[15px] font-semibold text-ink">
        {TITLE.howTo}
      </h3>
      {intro && <p className="mt-1 text-[13px] text-ink-2">{intro}</p>}
      <div className="mt-4">
        <Steps items={HOW_TO.map((s) => ({ title: s.title, status: s.text }))} />
      </div>
    </section>
  );
}

/** Подключений нет: что сделать, по порядку. */
function Empty() {
  return (
    <div className="grid grid-cols-2 gap-4 max-wide:grid-cols-1">
      <Card>
        <EmptyState icon={Bot} title={TITLE.empty} description={T.emptyIntro} />
      </Card>
      <Card>
        <HowTo />
      </Card>
    </div>
  );
}
